import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { calcularPuntosCPO, calcularCursadasAprobadasCPC } from '../utils/careerLogic';

const CPOContext = createContext(null);

// Clave única para persistencia y futuras migraciones
const STORAGE_KEY = 'cpo:v1';

const initialState = {
  orientacionId: '',
  // { [id]: { id, nombre, creditos, categoria, estado } }
  materias: {},
};

export function CPOProvider({ materias, orientacionFromApp, children }) {
  const persisted = useRef(false);

  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Migración: normalizar estados previos si existiera FINAL_APROBADO
        if (parsed && parsed.materias) {
          Object.values(parsed.materias).forEach((m) => {
            if (m && m.estado === 'FINAL_APROBADO') m.estado = 'CURSADA_APROBADA';
          });
        }
        return parsed;
      }
      // Migración básica desde clave previa si existía
      const legacyOrient = localStorage.getItem('cpo:orientation') || '';
      return legacyOrient ? { ...initialState, orientacionId: legacyOrient } : initialState;
    } catch {
      return initialState;
    }
  });

  // Sincronizar orientación con la que maneja la App si se provee
  useEffect(() => {
    if (!orientacionFromApp && orientacionFromApp !== '') return;
    const normalized = typeof orientacionFromApp === 'string'
      ? orientacionFromApp
      : orientacionFromApp && orientacionFromApp.nombre
        ? orientacionFromApp.nombre
        : orientacionFromApp && orientacionFromApp.id
          ? orientacionFromApp.id
          : '';
    setState((prev) => (prev.orientacionId === normalized ? prev : { ...prev, orientacionId: normalized }));
  }, [orientacionFromApp]);

  // Acciones del contexto
  const setOrientacion = useCallback((orientacionId) => {
    setState((prev) => (prev.orientacionId === orientacionId ? prev : { ...prev, orientacionId }));
  }, []);

  const upsertMateriaCPO = useCallback((materia) => {
    setState((prev) => ({
      ...prev,
      materias: {
        ...prev.materias,
        [materia.id]: {
          ...prev.materias[materia.id],
          ...materia,
        },
      },
    }));
  }, []);

  const toggleMateriaCPO = useCallback((id, payload = {}) => {
    setState((prev) => {
      const current = prev.materias[id] || { id, estado: 'NO_CURSADA', creditos: 0, categoria: 'CPO Libre Elección', nombre: id };
      const nextEstado = current.estado === 'CURSADA_APROBADA' ? 'NO_CURSADA' : 'CURSADA_APROBADA';
      return {
        ...prev,
        materias: {
          ...prev.materias,
          [id]: { ...current, ...payload, estado: nextEstado },
        },
      };
    });
  }, []);

  const removeMateriaCPO = useCallback((id) => {
    setState((prev) => {
      if (!prev.materias[id]) return prev;
      const { [id]: _omit, ...rest } = prev.materias;
      return { ...prev, materias: rest };
    });
  }, []);

  const resetCPO = useCallback(() => {
    setState(initialState);
  }, []);

  // Calcular puntos desde las materias guardadas en el contexto (modal CPO)
  const puntosDesdeContexto = useMemo(() => {
    const materiasDict = Object.values(state.materias).reduce((acc, m) => {
      acc[m.id] = {
        id: m.id,
        creditos: Number(m.creditos || 0),
        categoria: m.categoria || 'CPO Libre Elección',
        estado: m.estado || 'NO_CURSADA',
        notaFinal: -2, // considerar como cursada para el conteo de puntos
      };
      return acc;
    }, {});
    return calcularPuntosCPO(materiasDict, state.orientacionId);
  }, [state]);

  // Fallback: si no hay materias en el contexto, mantener el comportamiento actual (calcular sobre "materias" globales)
  const defaultPuntos = useMemo(() => ({
    obligatoriasComunesCursadas: 0,
    orientacionPrincipales: 0,
    orientacionDerivadas: 0,
    constitucionalProfundizado: 0,
    filosofia: 0,
    sociales: 0,
    procesalObligatorio: 0,
    procesalLibre: 0,
    libresGeneralesCPO: 0,
    integracion: 0,
    practico: 0,
    totalCPORequerido: 0,
    total: 0,
  }), []);

  const puntos = useMemo(() => {
    if (Object.keys(state.materias).length > 0) return puntosDesdeContexto;
    return materias ? calcularPuntosCPO(materias, state.orientacionId) : defaultPuntos;
  }, [puntosDesdeContexto, materias, state.orientacionId, defaultPuntos, state.materias]);

  const cpcCursadas = useMemo(() => (materias ? calcularCursadasAprobadasCPC(materias) : 0), [materias]);

  // Persistencia
  useEffect(() => {
    if (!persisted.current) persisted.current = true;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      // Mantener compatibilidad con clave antigua para quien la lea en otro lugar
      localStorage.setItem('cpo:orientation', state.orientacionId || '');
    } catch {}
  }, [state]);

  const value = useMemo(
    () => ({
      state,
      puntos,
      cpcCursadas,
      setOrientacion,
      upsertMateriaCPO,
      toggleMateriaCPO,
      removeMateriaCPO,
      resetCPO,
    }),
    [state, puntos, cpcCursadas, setOrientacion, upsertMateriaCPO, toggleMateriaCPO, removeMateriaCPO, resetCPO]
  );

  return <CPOContext.Provider value={value}>{children}</CPOContext.Provider>;
}

export function useCPOContext() {
  const ctx = useContext(CPOContext);
  if (!ctx) throw new Error('useCPOContext must be used within <CPOProvider>');
  return ctx;
}

